import { useCallback } from 'react'
import {
  getProjectAutofillDraft,
  postProjectAutofillDraftFile
} from '../../../../../../../services/projectAutofillDraftService.ts'
import type {
  DocumentationSlots,
  DraftMetadataFileEntry,
  RubroRow
} from '../../../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  applySlotPagesToRowDocumentation,
  DOCUMENTATION_SLOT_KEYS,
  documentationSlotSourcesHaveData,
  documentationSlotsHavePages,
  extractDraftMetadataFilesFromResponse,
  normalizeDocumentationFileIds,
  normalizeDocumentationSlots,
  normalizeDocumentationSlotSources,
  normalizePageList,
  pickNewFileIdAfterUpload,
  removeDocumentationChipPage
} from '../../../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import { findRubroRowIndex } from '../projectAutofillCardUtils.ts'

type SaveState = 'idle' | 'ok' | 'error'

function patchRubroRowAt(
  rows: RubroRow[],
  tempId: string,
  patch: (row: RubroRow) => RubroRow
): RubroRow[] {
  const idx = findRubroRowIndex(rows, tempId)
  if (idx < 0) return rows
  const next = [...rows]
  next[idx] = patch(rows[idx])
  return next
}

export function useProjectAutofillRubroDocumentation(args: {
  draftId: string | undefined
  metadataFiles: DraftMetadataFileEntry[]
  setRubros: React.Dispatch<React.SetStateAction<RubroRow[]>>
  setMetadataFilesOverride: React.Dispatch<React.SetStateAction<DraftMetadataFileEntry[] | null>>
  setUploadingSlotKey: React.Dispatch<React.SetStateAction<string | null>>
  setSaveState: React.Dispatch<React.SetStateAction<SaveState>>
}) {
  const { draftId, metadataFiles, setRubros, setMetadataFilesOverride, setUploadingSlotKey, setSaveState } = args

  const applyUploadedFilesList = useCallback(
    async (res: unknown): Promise<DraftMetadataFileEntry[] | null> => {
      let next = extractDraftMetadataFilesFromResponse(res)
      if ((!next || next.length === 0) && draftId) {
        try {
          const fresh = await getProjectAutofillDraft(String(draftId))
          next = extractDraftMetadataFilesFromResponse(fresh) ?? null
        } catch {
        }
      }
      if (next && next.length > 0) {
        setMetadataFilesOverride(next)
        return next
      }
      return null
    },
    [draftId, setMetadataFilesOverride]
  )

  const handleSelectRubroSlotFileId = useCallback(
    (tempId: string, slot: keyof DocumentationSlots, fileId: string | null) => {
      setRubros((prev) =>
        patchRubroRowAt(prev, tempId, (r) => {
          const cur = normalizeDocumentationFileIds(r.documentationFileIds ?? {})
          const next = { ...cur }
          const slotSrc = normalizeDocumentationSlotSources(r.documentationSlotSources ?? {})
          const nextSlotSrc = { ...slotSrc }
          delete nextSlotSrc[slot]
          const slotSourcesOut = documentationSlotSourcesHaveData(nextSlotSrc)
            ? nextSlotSrc
            : undefined
          if (fileId && fileId.trim()) {
            next[slot] = fileId.trim()
            const slotsCur = normalizeDocumentationSlots(r.documentationSlots ?? {})
            const nextSlots: DocumentationSlots = { ...slotsCur }
            delete nextSlots[slot]
            const hasAnySlots = documentationSlotsHavePages(nextSlots)
            const hasAny = DOCUMENTATION_SLOT_KEYS.some((k) => !!next[k])
            return {
              ...r,
              documentationFileIds: hasAny ? next : undefined,
              documentationSlots: hasAnySlots ? nextSlots : {},
              documentationSlotSources: slotSourcesOut
            }
          }
          delete next[slot]
          const hasAny = DOCUMENTATION_SLOT_KEYS.some((k) => !!next[k])
          return {
            ...r,
            documentationFileIds: hasAny ? next : undefined,
            documentationSlotSources: slotSourcesOut
          }
        })
      )
      setSaveState('idle')
    },
    [setRubros, setSaveState]
  )

  const handleRubroSlotPagesChange = useCallback(
    (tempId: string, slot: keyof DocumentationSlots, pages: number[]) => {
      setRubros((prev) =>
        patchRubroRowAt(prev, tempId, (r) => {
          const patch = applySlotPagesToRowDocumentation(r, slot, pages)
          return { ...r, ...patch }
        })
      )
      setSaveState('idle')
    },
    [setRubros, setSaveState]
  )

  const handleRubroDocumentationChipRemove = useCallback(
    (tempId: string, slot: keyof DocumentationSlots, page: number, fileId?: string) => {
      setRubros((prev) =>
        patchRubroRowAt(prev, tempId, (r) => {
          const patch = removeDocumentationChipPage(r, slot, page, fileId)
          return { ...r, ...patch }
        })
      )
      setSaveState('idle')
    },
    [setRubros, setSaveState]
  )

  const handleUploadRubroSlotFile = useCallback(
    async (tempId: string, slot: keyof DocumentationSlots, file: File) => {
      if (!draftId) return
      const key = `${tempId}:${slot}`
      setUploadingSlotKey(key)
      setSaveState('idle')
      try {
        const prevList = metadataFiles
        const res = await postProjectAutofillDraftFile(String(draftId), file)
        const next = await applyUploadedFilesList(res)
        if (next?.length) {
          const newId = pickNewFileIdAfterUpload(prevList, next)
          if (newId) {
            handleSelectRubroSlotFileId(tempId, slot, newId)
          }
        }
      } catch {
        setSaveState('error')
      } finally {
        setUploadingSlotKey(null)
      }
    },
    [
      draftId,
      metadataFiles,
      applyUploadedFilesList,
      handleSelectRubroSlotFileId,
      setUploadingSlotKey,
      setSaveState
    ]
  )

  return {
    applyUploadedFilesList,
    handleSelectRubroSlotFileId,
    handleRubroSlotPagesChange,
    handleRubroDocumentationChipRemove,
    handleUploadRubroSlotFile
  }
}
